import React, { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import {
  PropertyRentProps,
  PropertyUpdateDataApi,
  PublishCatalogs,
  UpdatePropertyRentInfoForm,
  UpdatePropertyRentStateForm,
} from '../models/propertyModel';
import {
  getPlansEndpoint,
  getPropertyToEditEndpoint,
  takeDownPropertyEndpoint,
  updatePropertyEndpoint,
  updatePropertyStatusEndpoint,
} from '../services/propertyService';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { PROPERTY_STATUS_VALUES } from '../constants/propertyConstants';
import { Option } from '../../../models/formModel';
import { listToSelectOptionAdapter } from '../../../adapters/listAdapter';
import { getOptionsBySelectedMonths } from '../adapters/propertyAdapter';
import { useToast } from '../../../components/toast/ToastProvider';
import { ESTIMATE_REFRESH_EVENT } from './usePropertyEstimate';
import { PROPERTY_REFRESH_EVENT, refreshProperty } from './propertyRefreshEvent';

// API may return invalid entries like ["[]"]; keep only valid numeric months
const sanitizeMonths = (months: (string | number)[] = []): number[] =>
  months
    .map((month) => (typeof month === 'string' ? parseInt(month) : month))
    .filter((month) => Number.isFinite(month));

const usePropertyRentPrices = (props: PropertyRentProps) => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const toast = useToast();

  const [formUpdateData, setFormUpdateData] = useState<PropertyUpdateDataApi>();
  const [loadingPlan, setLoadingPlan] = useState<boolean>(true);
  const [currentPlan, setCurrentPlan] = useState<Option>({
    label: 'Selecciona...',
    value: '',
  });
  const [catalogs, setCatalogs] = useState<PublishCatalogs>({
    plans: [],
  });

  const [loadingMonths, setLoadingMonths] = useState<boolean>(true);
  const [currentMonths, setCurrentMonths] = useState<any[]>([]);

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    register,
    reset,
    watch,
  } = useForm<UpdatePropertyRentInfoForm>({
    resolver: yupResolver(
      yup
        .object({
          price: yup
            .number()
            .min(0)
            .required('Precio de renta es requerido')
            .typeError('Precio de renta es requerido en formato numérico'),
          min_price: yup
            .number()
            .max(
              yup.ref('price'),
              'Precio de renta mínimo tiene que ser menor o igual al precio de renta',
            )
            .required('Precio mínimo de renta es requerido'),
          months: yup
            .array()
            .min(1, 'Debes seleccionar almenos una opción')
            .required('Debes seleccionar almenos una opción'),
          available_at: yup.string().required('Fecha es requerida'),
          plan_id: yup.string().required('Plan es requerida'),
        })
        .required(),
    ),
  });

  const loadExistingProperty = useCallback(async () => {
    const { status, data } = await callEndpoint(
      getPropertyToEditEndpoint(props.property_id, token),
    );
    if (status === 200) {
      const { data: formData } = fetchResponseAdapter(data);
      setFormUpdateData(formData);
      return formData;
    }
    return null;
  }, [callEndpoint, props.property_id, token]);

  const loadPlans = useCallback(async () => {
    const plans = await callEndpoint(getPlansEndpoint(token));
    if (plans.status !== 200) {
      return;
    }
    const plansResponse = fetchResponseAdapter(plans.data);
    setCatalogs({
      plans: listToSelectOptionAdapter(plansResponse.data, 'id', 'name'),
    });
    setLoadingPlan(false);
  }, [callEndpoint, token]);

  useEffect(() => {
    loadPlans().catch((e) => console.error(e));
  }, [loadPlans]);

  useEffect(() => {
    const reload = () => loadExistingProperty().catch((e) => console.error(e));
    reload();
    window.addEventListener(PROPERTY_REFRESH_EVENT, reload);
    return () => window.removeEventListener(PROPERTY_REFRESH_EVENT, reload);
  }, [loadExistingProperty]);

  useEffect(() => {
    if (formUpdateData) {
      setCurrentPlan({
        label: formUpdateData.plan ? formUpdateData.plan.name : '',
        value: formUpdateData.plan ? formUpdateData.plan.id : '',
      });
      setCurrentMonths(getOptionsBySelectedMonths(formUpdateData?.months || []));
      setLoadingMonths(false);
      reset({
        plan_id: formUpdateData.plan?.id ?? '',
        price: formUpdateData.price ?? null,
        min_price: formUpdateData.min_price ?? null,
        available_at: formUpdateData.available_at ?? null,
        months: sanitizeMonths(formUpdateData.months),
      });
    }
  }, [formUpdateData]);

  const updateMarketplaceRentPrices = async (
    form: UpdatePropertyRentInfoForm,
  ) => {
    // make sure that months are number on update already saved months
    form.months = sanitizeMonths(form.months);
    // map
    const { status } = await callEndpoint(
      updatePropertyEndpoint(form, props.property_id, token),
    );
    if (status === 200) {
      toast.success('Información de renta actualizada.');
      // Se queda en la pantalla: el estimate se recalcula en segundo plano y la tarjeta se refresca.
      window.dispatchEvent(new Event(ESTIMATE_REFRESH_EVENT));
      refreshProperty();
    } else {
      toast.error('No se pudo guardar. Intenta de nuevo.');
    }
  };

  const updateMarketplaceRentStatus = async (
    property_id: string,
    form: UpdatePropertyRentStateForm,
  ) => {
    let requestStatus = null;
    if (form.status === PROPERTY_STATUS_VALUES.PUBLISHED) {
      const { status } = await callEndpoint(
        updatePropertyStatusEndpoint(props.property_id, form, token),
      );
      requestStatus = status;
    } else {
      const { status } = await callEndpoint(
        takeDownPropertyEndpoint(props.property_id, form, token),
      );
      requestStatus = status;
    }
    if (requestStatus !== 200) {
      toast.error('No se pudo actualizar el estado de la propiedad.');
      return;
    }
    toast.success('Estado de la propiedad actualizado.');
    refreshProperty();
  };

  return {
    control,
    register,
    errors,
    setValue,
    watch,
    currency: formUpdateData?.currency ?? 'USD',
    handleSubmit,
    isLoading,
    loadingPlan,
    currentPlan,
    loadingMonths,
    currentMonths,
    catalogs,
    updateMarketplaceRentPrices,
    updateMarketplaceRentStatus,
  };
};

export default usePropertyRentPrices;
